import * as React from 'react';
import Flex from 'antd/lib/flex';
import Text from 'antd/lib/typography/Text';
import { Link } from 'react-router-dom';
import { getNavSectionTitleStyle, getNavDropdownMenuStyle, navFlexItemStyle } from '../../styles/globalStyles';
import { useSidebarColors } from '../../styles/themeMode';
import { HoverableNavItem } from '../shared/HoverableNavItem';
import { createReleaseOverviewRoute } from '../../../helpers/routeHelpers';
import { ReleaseStatus } from '../../../types';
import { isReleaseSkipped, statusColorForReleaseStatus } from '../../../helpers';
import { StatusNodeDot } from '../../StatusNode';

export interface ReleaseListItem {
    id: string;
    releaseId: string;
    version: string;
    displayableVersion: string;
    status: ReleaseStatus;
}

interface VersionItemProps {
    orgId: string;
    appId: string;
    release: ReleaseListItem;
    isSelected: boolean;
    onSelect: () => void;
}

const VersionItem: React.FC<VersionItemProps> = ({ orgId, appId, release, isSelected, onSelect }) => {
    const isSkipped = isReleaseSkipped(release.status);
    const statusColor = statusColorForReleaseStatus(release.status);
    const sc = useSidebarColors();
    return (
        <Link
            to={createReleaseOverviewRoute(orgId, appId, release.releaseId)}
            onClick={onSelect}
            style={{ textDecoration: 'none' }}
        >
            <HoverableNavItem isSelected={isSelected}>
                <Flex align="center" gap={12} style={navFlexItemStyle}>
                    <StatusNodeDot
                        statusColor={statusColor}
                        size={12}
                        outerBorderColor="transparent"
                        outerBorderWidth={0}
                    />
                    <Text
                        style={{
                            ...navFlexItemStyle,
                            color: isSkipped ? sc.textMuted : sc.text,
                            fontWeight: isSelected ? 600 : 400,
                            textDecoration: isSkipped ? 'line-through' : 'none',
                        }}
                        ellipsis
                    >
                        {release.displayableVersion}
                    </Text>
                </Flex>
            </HoverableNavItem>
        </Link>
    );
};

export interface VersionDropdownProps {
    orgId: string;
    appId: string;
    currentReleaseId: string;
    upcomingReleases: ReleaseListItem[];
    currentReleases: ReleaseListItem[];
    completedReleases: ReleaseListItem[];
    onSelect: () => void;
}

export const VersionDropdown: React.FC<VersionDropdownProps> = ({
    orgId,
    appId,
    currentReleaseId,
    upcomingReleases,
    currentReleases,
    completedReleases,
    onSelect,
}) => {
    const sc = useSidebarColors();
    const renderReleaseGroup = (title: string, releases: typeof upcomingReleases) => {
        if (!releases || releases.length === 0) return null;

        return (
            <div style={{ marginBottom: 16 }}>
                <Text style={getNavSectionTitleStyle(sc)}>{title}</Text>
                <div style={{ paddingTop: 8 }}>
                    {releases.map((release) => (
                        <VersionItem
                            key={release.id}
                            orgId={orgId}
                            appId={appId}
                            release={release}
                            isSelected={release.id === currentReleaseId}
                            onSelect={onSelect}
                        />
                    ))}
                </div>
            </div>
        );
    };

    return (
        <div
            style={{
                ...getNavDropdownMenuStyle(sc),
                minWidth: '18rem',
                maxHeight: '400px',
                overflowY: 'auto',
                padding: '12px 0',
            }}
        >
            {renderReleaseGroup('Upcoming', upcomingReleases)}
            {renderReleaseGroup('Active', currentReleases)}
            {renderReleaseGroup('Completed', completedReleases.slice(0, 10))}
        </div>
    );
};
